import { format } from "date-fns";

export type TimezoneOption = { value: string; label: string };

export const LEGACY_PROFILE_TIMEZONE_DEFAULT = "America/New_York";

const fallbackTimezones = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "America/Toronto",
  "America/Vancouver",
  "America/Mexico_City",
  "Pacific/Honolulu",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Europe/Rome",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Asia/Singapore",
  "Asia/Hong_Kong",
  "Australia/Sydney",
  "Australia/Brisbane",
];

const labelFormatterDate = new Date("2026-01-15T12:00:00.000Z");

const formatTimezoneLocation = (zone: string): string => {
  if (zone === "UTC") return "UTC";
  return zone
    .split("/")
    .map((part) => part.replace(/_/g, " "))
    .join("/");
};

const formatTimezoneCity = (zone: string): string => {
  if (zone === "UTC") return "UTC";
  const parts = zone.split("/");
  return (parts[parts.length - 1] || zone).replace(/_/g, " ");
};

const getTimezoneName = (zone: string): string | null => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      timeZoneName: "longGeneric",
    }).formatToParts(labelFormatterDate);
    return parts.find((part) => part.type === "timeZoneName")?.value ?? null;
  } catch {
    return null;
  }
};

const formatTimezoneLabel = (zone: string): string => {
  if (zone === "UTC") return "UTC";

  const city = formatTimezoneCity(zone);
  const location = formatTimezoneLocation(zone);
  const timeZoneName = getTimezoneName(zone);
  return timeZoneName && timeZoneName !== location
    ? `${city} - ${timeZoneName} (${location})`
    : `${city} (${location})`;
};

export const getTimezones = (): TimezoneOption[] => {
  const supportedValuesOf = (Intl as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
  const zones = typeof supportedValuesOf === "function"
    ? supportedValuesOf("timeZone")
    : fallbackTimezones;

  return Array.from(new Set(zones))
    .map((zone) => ({ value: zone, label: formatTimezoneLabel(zone) }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

const normalizeTimezone = (timezone?: string | null): string | null => {
  const trimmed = timezone?.trim();
  return trimmed || null;
};

export const shouldAutoDetectProfileTimezone = (params: {
  profileTimezone?: string | null;
  timezoneManuallySetAt?: string | null;
  browserTimezone?: string | null;
}): boolean => {
  const profileTimezone = normalizeTimezone(params.profileTimezone);
  const browserTimezone = normalizeTimezone(params.browserTimezone) || getBrowserTimezone();

  if (!browserTimezone) return false;
  if (params.timezoneManuallySetAt) return false;
  if (!profileTimezone) return true;

  return (
    profileTimezone === LEGACY_PROFILE_TIMEZONE_DEFAULT &&
    browserTimezone !== LEGACY_PROFILE_TIMEZONE_DEFAULT
  );
};

export const resolveProfileTimezone = (params: {
  profileTimezone?: string | null;
  timezoneManuallySetAt?: string | null;
  browserTimezone?: string | null;
}): string => {
  const browserTimezone = normalizeTimezone(params.browserTimezone) || getBrowserTimezone();
  const profileTimezone = normalizeTimezone(params.profileTimezone);

  if (shouldAutoDetectProfileTimezone({ ...params, browserTimezone })) {
    return browserTimezone;
  }

  return profileTimezone || browserTimezone;
};

const getTimeZoneOffset = (date: Date, timeZone: string): number => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const parts = formatter.formatToParts(date);
  const values: Record<string, string> = {};
  for (const part of parts) {
    if (part.type !== "literal") {
      values[part.type] = part.value;
    }
  }

  const utcFromTz = Date.UTC(
    Number(values.year),
    Number(values.month) - 1,
    Number(values.day),
    Number(values.hour),
    Number(values.minute),
    Number(values.second)
  );

  return (utcFromTz - date.getTime()) / 60000;
};

type ZonedDateParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  weekday: number;
};

export const getZonedDateParts = (date: Date, timeZone: string): ZonedDateParts => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    weekday: "short",
  });

  const parts = formatter.formatToParts(date);
  const values: Record<string, string> = {};
  for (const part of parts) {
    if (part.type !== "literal") {
      values[part.type] = part.value;
    }
  }

  const weekdayMap: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };

  return {
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
    hour: Number(values.hour),
    minute: Number(values.minute),
    second: Number(values.second),
    weekday: weekdayMap[values.weekday] ?? 0,
  };
};

const createDateInTimeZone = (
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  timeZone: string
): Date => {
  const utcGuess = Date.UTC(year, month, day, hour, minute, second);
  let date = new Date(utcGuess);
  const offset = getTimeZoneOffset(date, timeZone);
  let utcMillis = utcGuess - offset * 60000;

  date = new Date(utcMillis);
  const offset2 = getTimeZoneOffset(date, timeZone);
  if (offset2 !== offset) {
    utcMillis = utcGuess - offset2 * 60000;
  }

  return new Date(utcMillis);
};

const getIcalTimezoneId = (icalTime: any): string | null => {
  const tz = icalTime?.timezone ?? icalTime?.zone;
  if (!tz) return null;
  if (typeof tz === "string") return tz;
  if (typeof tz?.tzid === "string") return tz.tzid;
  return null;
};

/**
 * Extracts a Date from an ICAL.Time object with proper timezone conversion.
 *
 * Handles three ICS time formats correctly:
 * 1. UTC times (Z suffix): DTSTART:20170706T200000Z → converts from UTC to browser timezone
 * 2. TZID times: DTSTART;TZID=America/Los_Angeles:20231206T120000 → converts from specified TZ to browser TZ
 * 3. Floating times (no TZ info): DTSTART:20231206T120000 → keeps as-is in browser timezone
 *
 * @param icalTime - ICAL.Time object from ical.js
 * @param fallbackTimeZone - Browser timezone to use for floating times (defaults to browser's timezone)
 * @returns Date object representing the correct moment in time (stored as UTC internally)
 */
export const extractLocalDateTime = (icalTime: any, fallbackTimeZone?: string): Date => {
  const browserTz = fallbackTimeZone || getBrowserTimezone();

  // Extract time components from ICAL.Time object
  const year = icalTime.year;
  const month = icalTime.month - 1; // JS months are 0-indexed, ICAL months are 1-indexed
  const day = icalTime.day;
  const hour = icalTime.hour;
  const minute = icalTime.minute;
  const second = icalTime.second || 0;

  // Detect timezone type from the ICAL.Time zone property
  const zoneTzid = getIcalTimezoneId(icalTime);

  // Case 1: UTC time (DTSTART:20170706T200000Z)
  if (zoneTzid === 'UTC' || zoneTzid === 'Z') {
    // Create a UTC Date object - this is the correct moment in time
    return new Date(Date.UTC(year, month, day, hour, minute, second));
  }

  // Case 2: TZID time (DTSTART;TZID=America/Los_Angeles:20231206T120000)
  if (zoneTzid && zoneTzid !== 'floating') {
    try {
      // Create a date representing the wall-clock time in the source timezone
      // This returns a UTC Date that represents the correct moment in time
      const dateInSourceTz = createDateInTimeZone(year, month, day, hour, minute, second, zoneTzid);
      return dateInSourceTz;
    } catch (err) {
      console.warn(`[extractLocalDateTime] Failed to parse timezone ${zoneTzid}, treating as floating:`, err);
      // Fall through to floating time handling
    }
  }

  // Case 3: Floating time (DTSTART:20231206T120000 - no Z, no TZID)
  // Interpret the wall-clock time as being in the browser's timezone
  // This preserves the intended local time - if it says 2:00 PM, it stays 2:00 PM
  return new Date(year, month, day, hour, minute, second);
};

/**
 * Extracts a Date from an ICAL.Time object with timezone awareness.
 * This is now just an alias for extractLocalDateTime which handles all cases.
 *
 * @deprecated Use extractLocalDateTime directly - it now handles all timezone cases
 */
export const extractEventDateTime = (icalTime: any, fallbackTimeZone: string): Date => {
  return extractLocalDateTime(icalTime, fallbackTimeZone);
};

export const getDateKeyInTimeZone = (date: Date, timeZone?: string): string => {
  return formatInTimeZone(date, "yyyy-MM-dd", timeZone);
};

export const startOfDayInTimeZone = (date: Date, timeZone?: string): Date => {
  const resolvedTimeZone = timeZone || getBrowserTimezone();
  const parts = getZonedDateParts(date, resolvedTimeZone);
  return createDateInTimeZone(parts.year, parts.month - 1, parts.day, 0, 0, 0, resolvedTimeZone);
};

export const endOfDayInTimeZone = (date: Date, timeZone?: string): Date => {
  const resolvedTimeZone = timeZone || getBrowserTimezone();
  const parts = getZonedDateParts(date, resolvedTimeZone);
  const nextDay = createDateInTimeZone(parts.year, parts.month - 1, parts.day + 1, 0, 0, 0, resolvedTimeZone);
  return new Date(nextDay.getTime() - 1);
};

export const addDaysInTimeZone = (date: Date, days: number, timeZone?: string): Date => {
  const resolvedTimeZone = timeZone || getBrowserTimezone();
  const parts = getZonedDateParts(date, resolvedTimeZone);
  return createDateInTimeZone(
    parts.year,
    parts.month - 1,
    parts.day + days,
    parts.hour,
    parts.minute,
    parts.second,
    resolvedTimeZone
  );
};

export const combineDateAndTimeInTimeZone = (
  date: Date,
  time: string,
  timeZone?: string
): Date => {
  const resolvedTimeZone = timeZone || getBrowserTimezone();
  const [hourStr, minuteStr] = time.split(":");
  const hour = Number(hourStr);
  const minute = Number(minuteStr);
  const parts = getZonedDateParts(date, resolvedTimeZone);

  return createDateInTimeZone(
    parts.year,
    parts.month - 1,
    parts.day,
    Number.isFinite(hour) ? hour : 0,
    Number.isFinite(minute) ? minute : 0,
    0,
    resolvedTimeZone
  );
};

export const createDateTimeInTimeZone = (
  dateValue: string,
  timeValue: string,
  timeZone?: string,
): Date | null => {
  const resolvedTimeZone = timeZone || getBrowserTimezone();
  const [yearStr, monthStr, dayStr] = dateValue.split("-");
  const [hourStr, minuteStr] = timeValue.split(":");

  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  const hour = Number(hourStr);
  const minute = Number(minuteStr);

  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute)
  ) {
    return null;
  }

  return createDateInTimeZone(
    year,
    month - 1,
    day,
    hour,
    minute,
    0,
    resolvedTimeZone,
  );
};

export const startOfWeekInTimeZone = (
  date: Date,
  timeZone?: string,
  weekStartsOn: number = 1
): Date => {
  const resolvedTimeZone = timeZone || getBrowserTimezone();
  const parts = getZonedDateParts(date, resolvedTimeZone);
  const diff = (parts.weekday - weekStartsOn + 7) % 7;
  return createDateInTimeZone(
    parts.year,
    parts.month - 1,
    parts.day - diff,
    0,
    0,
    0,
    resolvedTimeZone
  );
};

/**
 * DEPRECATED: This function was fundamentally broken. JavaScript Date objects
 * are always UTC internally - you cannot create a "zoned" Date.
 * Use formatInTimeZone() for display instead.
 *
 * Keeping for backward compatibility but it now just returns the date as-is.
 */
export const toZonedDate = (date: Date, _timeZone?: string): Date => {
  // Just return the date - it's already UTC
  // Any timezone conversion should happen at display time only
  return date;
};

/**
 * Formats a Date in a specific timezone using Intl.DateTimeFormat.
 * This properly handles timezone conversion for display.
 */
export const formatInTimeZone = (date: Date, formatString: string, timeZone?: string): string => {
  const resolvedTimeZone = timeZone || getBrowserTimezone();

  try {
    if (formatString === "yyyy-MM-dd") {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: resolvedTimeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).formatToParts(date);
      const values: Record<string, string> = {};
      for (const part of parts) {
        if (part.type !== "literal") {
          values[part.type] = part.value;
        }
      }
      return `${values.year}-${values.month}-${values.day}`;
    }

    if (formatString === "EEE, MMM d · h:mm a") {
      const datePart = new Intl.DateTimeFormat("en-US", {
        timeZone: resolvedTimeZone,
        weekday: "short",
        month: "short",
        day: "numeric",
      }).format(date);
      const timePart = new Intl.DateTimeFormat("en-US", {
        timeZone: resolvedTimeZone,
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }).format(date);
      return `${datePart} · ${timePart}`;
    }

    if (formatString === "h:mm") {
      return new Intl.DateTimeFormat("en-US", {
        timeZone: resolvedTimeZone,
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
        .format(date)
        .replace(/\s?[AP]M$/i, "");
    }

    if (formatString === "EEE d") {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: resolvedTimeZone,
        weekday: "short",
        day: "numeric",
      }).formatToParts(date);
      const values: Record<string, string> = {};
      for (const part of parts) {
        if (part.type !== "literal") {
          values[part.type] = part.value;
        }
      }
      return `${values.weekday} ${values.day}`;
    }

    // Map common date-fns format strings to Intl.DateTimeFormat options
    const formatMap: Record<string, Intl.DateTimeFormatOptions> = {
      'h:mm a': { hour: 'numeric', minute: '2-digit', hour12: true },
      'HH:mm': { hour: '2-digit', minute: '2-digit', hour12: false },
      "MMM d 'at' h:mm a": { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true },
      'd': { day: 'numeric' },
      'MMM d': { month: 'short', day: 'numeric' },
      'MMMM d': { month: 'long', day: 'numeric' },
      'EEE, MMM d': { weekday: 'short', month: 'short', day: 'numeric' },
      'EEEE, MMM d': { weekday: 'long', month: 'short', day: 'numeric' },
      'EEEE, MMMM d': { weekday: 'long', month: 'long', day: 'numeric' },
      'EEE': { weekday: 'short' },
      'EEEE': { weekday: 'long' },
      'MMM d, yyyy': { month: 'short', day: 'numeric', year: 'numeric' },
      'EEEE, MMMM d, yyyy': { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' },
    };

    const options = formatMap[formatString];

    if (options) {
      // Use Intl.DateTimeFormat for proper timezone handling
      const formatter = new Intl.DateTimeFormat('en-US', {
        ...options,
        timeZone: resolvedTimeZone,
      });
      return formatter.format(date);
    }

    // Fallback: use date-fns format on UTC date
    // This is not timezone-aware but prevents crashes
    return format(date, formatString);
  } catch (error) {
    // If timezone is invalid or formatting fails, fall back to date-fns
    console.warn('formatInTimeZone failed, using fallback:', error);
    return format(date, formatString);
  }
};
